
import { useRef, useState, useEffect, useMemo } from "react";

const BREAKPOINTS = { lg: 1024, md: 768, sm: 0 } as const;
type Bp = keyof typeof BREAKPOINTS;
function bpForWidth(w: number): Bp {
  if (w >= BREAKPOINTS.lg) return "lg";
  if (w >= BREAKPOINTS.md) return "md";
  return "sm";
}
import { Responsive as ReactGridLayout } from "react-grid-layout";
import { WidgetRenderer } from "./WidgetRenderer";
import { X } from "lucide-react";
import type { FBPost, IGPost, GA4Data } from "@/lib/types";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

export interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

interface WidgetGridProps {
  widgetIds: string[];
  layouts: LayoutItem[];
  onLayoutChange: (layouts: LayoutItem[]) => void;
  onRemove: (id: string) => void;
  fb: FBPost[];
  ig: IGPost[];
  ga4: GA4Data[];
  allFb: FBPost[];
  allIg: IGPost[];
  dateFrom: string;
  dateTo: string;
}

function useWidth(ref: React.RefObject<HTMLDivElement | null>) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    obs.observe(ref.current);
    setWidth(ref.current.offsetWidth);
    return () => obs.disconnect();
  }, [ref]);
  return width;
}

export function WidgetGrid({
  widgetIds, layouts, onLayoutChange, onRemove,
  fb, ig, ga4, allFb, allIg, dateFrom, dateTo,
}: WidgetGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useWidth(containerRef);
  // Derive current breakpoint from width directly — avoids the race where
  // onLayoutChange fires before onBreakpointChange has updated state, which
  // would persist a non-lg layout into the lg slot.
  const breakpoint: Bp = width > 0 ? bpForWidth(width) : "lg";

  // Derive md (2-col) and sm (1-col) layouts from widgetIds insertion order
  // so on mobile/tablet widgets stack in the order user added them, not by
  // arbitrary y-coords from the desktop layout.
  const responsiveLayouts = useMemo(() => {
    const byId = new Map(layouts.map((l) => [l.i, l]));

    // 1-col mobile — stack in widgetIds order
    const sm: LayoutItem[] = [];
    let smY = 0;
    for (const id of widgetIds) {
      const src = byId.get(id);
      const h = src?.h ?? 1;
      sm.push({ i: id, x: 0, y: smY, w: 1, h, minW: 1, minH: src?.minH });
      smY += h;
    }

    // 2-col tablet — pack pairs in widgetIds order
    const md: LayoutItem[] = [];
    let mdX = 0, mdY = 0, mdRowH = 0;
    for (const id of widgetIds) {
      const src = byId.get(id);
      const w = Math.min(src?.w ?? 1, 2);
      const h = src?.h ?? 1;
      if (mdX + w > 2) { mdX = 0; mdY += mdRowH; mdRowH = 0; }
      md.push({ i: id, x: mdX, y: mdY, w, h, minW: 1, minH: src?.minH });
      mdX += w;
      mdRowH = Math.max(mdRowH, h);
    }

    return { lg: layouts, md, sm };
  }, [layouts, widgetIds]);

  const gridProps = useMemo(() => ({
    className: "widget-grid",
    width,
    layouts: responsiveLayouts,
    breakpoints: BREAKPOINTS,
    cols: { lg: 4, md: 2, sm: 1 },
    rowHeight: 80,
    containerPadding: [0, 0] as [number, number],
    margin: [16, 16] as [number, number],
    // Persist only when user is actually on desktop; mobile/tablet are derived,
    // saving them would overwrite the desktop layout.
    onLayoutChange: (layout: LayoutItem[]) => {
      if (breakpoint === "lg") onLayoutChange(layout);
    },
    draggableHandle: ".widget-drag-handle",
    isDraggable: breakpoint === "lg",
    isResizable: breakpoint === "lg",
  }), [width, responsiveLayouts, onLayoutChange, breakpoint]);

  return (
    <div ref={containerRef}>
      {width > 0 && (
        // @ts-expect-error react-grid-layout types mismatch
        <ReactGridLayout {...gridProps}>
          {widgetIds.map((id) => (
            <div key={id} className="relative group">
              <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="widget-drag-handle cursor-grab active:cursor-grabbing bg-card/80 backdrop-blur rounded-br-lg px-2 py-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                  ⠿ přesunout
                </div>
                <button
                  onClick={() => onRemove(id)}
                  className="bg-destructive/80 backdrop-blur text-white rounded-bl-lg p-1 hover:bg-destructive cursor-pointer"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="h-full [&>*]:h-full [&>*>*]:h-full overflow-hidden">
                <WidgetRenderer
                  widgetId={id}
                  fb={fb} ig={ig} ga4={ga4}
                  allFb={allFb} allIg={allIg}
                  dateFrom={dateFrom} dateTo={dateTo}
                />
              </div>
            </div>
          ))}
        </ReactGridLayout>
      )}
    </div>
  );
}
